// src/utils/tagsApi.ts
// Thin HTTP wrappers around the tag/category/assignment routes.
import { api } from './api.ts';
import type { Tag, TagCategory, PathStyle, MarkerStyle } from '../types/position.ts';

function withUser(params: URLSearchParams, scopeUserId?: number | null) {
  if (scopeUserId != null) params.set('scope_user_id', String(scopeUserId));
  return params;
}

// ---------- categories ----------

export async function fetchTagCategories(scopeUserId?: number | null): Promise<TagCategory[]> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.get(q ? `/api/tag-categories?${q}` : '/api/tag-categories');
  return res.data as TagCategory[];
}

export async function createTagCategory(
  body: { scope: 'user' | 'global'; tag_category: string; shadows_category_id?: number | null },
  scopeUserId?: number | null,
): Promise<TagCategory> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.post(q ? `/api/tag-categories?${q}` : '/api/tag-categories', body);
  return res.data;
}

export async function updateTagCategory(
  id: number,
  body: { tag_category?: string; shadows_category_id?: number | null },
  scopeUserId?: number | null,
): Promise<TagCategory> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.patch(q ? `/api/tag-categories/${id}?${q}` : `/api/tag-categories/${id}`, body);
  return res.data;
}

export async function tagCategoryImpact(id: number): Promise<{ tagCount: number; assignmentCount: number; shadowingUserCount: number | null }> {
  const res = await api.get(`/api/tag-categories/${id}/impact`);
  return res.data;
}

export async function deleteTagCategory(id: number, scopeUserId?: number | null): Promise<void> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  p.set('cascade', 'true');
  await api.delete(`/api/tag-categories/${id}?${p.toString()}`);
}

// ---------- tags ----------

export async function fetchTags(categoryId?: number | null, scopeUserId?: number | null): Promise<Tag[]> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  if (categoryId != null) p.set('category_id', String(categoryId));
  const q = p.toString();
  const res = await api.get(q ? `/api/tags?${q}` : '/api/tags');
  return res.data as Tag[];
}

export interface CreateTagBody {
  category_id: number;
  parent_tag_id?: number | null;
  tag: string;
  path_style?: PathStyle;
  marker_style?: MarkerStyle;
  sort_order?: number | null;
}

export async function createTag(body: CreateTagBody, scopeUserId?: number | null): Promise<Tag> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.post(q ? `/api/tags?${q}` : '/api/tags', body);
  return res.data;
}

export interface UpdateTagBody {
  tag?: string;
  parent_tag_id?: number | null;
  category_id?: number;
  path_style?: PathStyle;
  marker_style?: MarkerStyle;
  sort_order?: number | null;
}

export async function updateTag(
  id: number,
  body: UpdateTagBody,
  opts: { scopeUserId?: number | null; resolveDropMoving?: boolean } = {},
): Promise<Tag> {
  const p = withUser(new URLSearchParams(), opts.scopeUserId);
  if (opts.resolveDropMoving) p.set('resolve', 'drop_moving');
  const q = p.toString();
  const res = await api.patch(q ? `/api/tags/${id}?${q}` : `/api/tags/${id}`, body);
  return res.data;
}

export async function tagImpact(id: number): Promise<{ descendantCount: number; assignmentCount: number }> {
  const res = await api.get(`/api/tags/${id}/impact`);
  return res.data;
}

export async function deleteTag(id: number, scopeUserId?: number | null): Promise<void> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  p.set('cascade', 'true');
  await api.delete(`/api/tags/${id}?${p.toString()}`);
}

export interface BulkTagImpact {
  subtreeTagCount: number;
  assignmentCount: number;
  topLevel: { id: number; tag: string }[];
}

export async function bulkTagImpact(ids: number[], scopeUserId?: number | null): Promise<BulkTagImpact> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.post(q ? `/api/tags/impact?${q}` : '/api/tags/impact', { ids });
  return res.data;
}

export async function bulkDeleteTags(ids: number[], scopeUserId?: number | null): Promise<void> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  p.set('cascade', 'true');
  await api.post(`/api/tags/delete?${p.toString()}`, { ids });
}

export async function bulkReparentTags(ids: number[], parentTagId: number | null, scopeUserId?: number | null): Promise<{ moved: number }> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.post(q ? `/api/tags/reparent?${q}` : '/api/tags/reparent', { ids, parent_tag_id: parentTagId });
  return res.data;
}

// ---------- assignments ----------

export async function assignPositionTag(positionId: number, tagId: number, scopeUserId?: number | null): Promise<void> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  await api.post(
    q ? `/api/positions/${positionId}/tags?${q}` : `/api/positions/${positionId}/tags`,
    { tag_id: tagId },
  );
}

export async function unassignPositionTag(positionId: number, tagId: number, scopeUserId?: number | null): Promise<void> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  await api.delete(
    q ? `/api/positions/${positionId}/tags/${tagId}?${q}` : `/api/positions/${positionId}/tags/${tagId}`,
  );
}

export async function bulkAssignTag(positionIds: number[], tagId: number, scopeUserId?: number | null): Promise<{ updatedCount: number }> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.post(
    q ? `/api/position-tags/bulk?${q}` : '/api/position-tags/bulk',
    { positionIds, tagId },
  );
  return res.data;
}

export async function bulkClearCategory(positionIds: number[], categoryId: number, scopeUserId?: number | null): Promise<{ removedCount: number }> {
  const p = withUser(new URLSearchParams(), scopeUserId);
  const q = p.toString();
  const res = await api.delete(
    q ? `/api/position-tags/bulk?${q}` : '/api/position-tags/bulk',
    { data: { positionIds, categoryId } },
  );
  return res.data;
}
